/**
 * Ring PRO variant IDs and checkout URLs.
 * Source: pro-variants/*.csv. Only 6 base stores in source; expanded for all stores at runtime.
 *
 * Structure: Color → Size → { E: engraved, N: non-engraved } → Store → { id, url }
 */
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { deepExpandStoreAliases } from 'content/products/storeAliasHelper';

export type VariantData = { id: string; url: string };
export type StoreVariants = Partial<Record<ShopifyStore, VariantData>>;

/** Base stores only (IN, AE, ROW, US, EU, GB). Aliased stores filled via deepExpandStoreAliases. */
const BASE_STORES: ShopifyStore[] = [
  ShopifyStore.US,
  ShopifyStore.IN,
  ShopifyStore.ROW,
  ShopifyStore.EU,
  ShopifyStore.GB,
  ShopifyStore.AE,
];

const PLACEHOLDER_URL = '';

/** Convert numeric variant ID to Shopify GID format for cart/checkout. */
function toShopifyGid(id: string): string {
  return id.startsWith('gid://') ? id : `gid://shopify/ProductVariant/${id}`;
}

/** Ring variant IDs by base store: color -> size -> id (from pro-variants CSVs). E and N use same id. */
const RING_IDS: Partial<Record<ShopifyStore, Record<string, Record<number, string>>>> = {
  [ShopifyStore.US]: {
    BLACK: {
      5: '42613670477920',
      6: '42613670510688',
      7: '42613670543456',
      8: '42613670576224',
      9: '42613670608992',
      10: '42613670641760',
      11: '42613670674528',
      12: '42613670707296',
      13: '42613670740064',
      14: '42613670772832',
      99: '42613670805600',
    },
    GOLD: {
      5: '42613671198816',
      6: '42613671231584',
      7: '42613671264352',
      8: '42613671297120',
      9: '42613671329888',
      10: '42613671362656',
      11: '42613671395424',
      12: '42613671428192',
      13: '42613671460960',
      14: '42613671493728',
      99: '42613671526496',
    },
    SILVER: {
      5: '42613671559264',
      6: '42613671592032',
      7: '42613671624800',
      8: '42613671657568',
      9: '42613671690336',
      10: '42613671723104',
      11: '42613671755872',
      12: '42613671788640',
      13: '42613671821408',
      14: '42613671854176',
      99: '42613671886944',
    },
    TITANIUM: {
      5: '42613671919712',
      6: '42613671952480',
      7: '42613671985248',
      8: '42613672018016',
      9: '42613672050784',
      10: '42613672083552',
      11: '42613672116320',
      12: '42613672149088',
      13: '42613672181856',
      14: '42613672214624',
      99: '42613672247392',
    },
  },
  [ShopifyStore.IN]: {
    BLACK: {
      5: '44694393913414',
      6: '44694394208326',
      7: '44694394503238',
      8: '44694394798150',
      9: '44694395093062',
      10: '44694395387974',
      11: '44694395682886',
      12: '44694395977798',
      13: '44694396272710',
      14: '44694396567622',
      99: '44694396862534',
    },
    GOLD: {
      5: '44694393946182',
      6: '44694394241094',
      7: '44694394536006',
      8: '44694394830918',
      9: '44694395125830',
      10: '44694395420742',
      11: '44694395715654',
      12: '44694396010566',
      13: '44694396305478',
      14: '44694396600390',
      99: '44694396895302',
    },
    SILVER: {
      5: '44694393978950',
      6: '44694394273862',
      7: '44694394568774',
      8: '44694394863686',
      9: '44694395158598',
      10: '44694395453510',
      11: '44694395748422',
      12: '44694396043334',
      13: '44694396338246',
      14: '44694396633158',
      99: '44694396928070',
    },
    TITANIUM: {
      5: '44694394077254',
      6: '44694394372166',
      7: '44694394667078',
      8: '44694394961990',
      9: '44694395256902',
      10: '44694395551814',
      11: '44694395846726',
      12: '44694396141638',
      13: '44694396436550',
      14: '44694396731462',
      99: '44694397026374',
    },
  },
  [ShopifyStore.ROW]: {
    BLACK: {
      5: '44021935571004',
      6: '44021935833148',
      7: '44021936095292',
      8: '44021936357436',
      9: '44021936619580',
      10: '44021936881724',
      11: '44021937143868',
      12: '44021937406012',
      13: '44021937668156',
      14: '44021937930300',
      99: '44021938192444',
    },
    GOLD: {
      5: '44021935603772',
      6: '44021935865916',
      7: '44021936128060',
      8: '44021936390204',
      9: '44021936652348',
      10: '44021936914492',
      11: '44021937176636',
      12: '44021937438780',
      13: '44021937700924',
      14: '44021937963068',
      99: '44021938225212',
    },
    SILVER: {
      5: '44021935636540',
      6: '44021935898684',
      7: '44021936160828',
      8: '44021936422972',
      9: '44021936685116',
      10: '44021936947260',
      11: '44021937209404',
      12: '44021937471548',
      13: '44021937733692',
      14: '44021937995836',
      99: '44021938257980',
    },
    TITANIUM: {
      5: '44021935702076',
      6: '44021935964220',
      7: '44021936226364',
      8: '44021936488508',
      9: '44021936750652',
      10: '44021937012796',
      11: '44021937274940',
      12: '44021937537084',
      13: '44021937799228',
      14: '44021938061372',
      99: '44021938323516',
    },
  },
  [ShopifyStore.EU]: {
    BLACK: {
      5: '57375791382902',
      6: '57375791612278',
      7: '57375791841654',
      8: '57375792071030',
      9: '57375792300406',
      10: '57375792529782',
      11: '57375792759158',
      12: '57375792988534',
      13: '57375793217910',
      14: '57375793447286',
      99: '57375793676662',
    },
    GOLD: {
      5: '57375791415670',
      6: '57375791645046',
      7: '57375791874422',
      8: '57375792103798',
      9: '57375792333174',
      10: '57375792562550',
      11: '57375792791926',
      12: '57375793021302',
      13: '57375793250678',
      14: '57375793480054',
      99: '57375793709430',
    },
    SILVER: {
      5: '57375791448438',
      6: '57375791677814',
      7: '57375791907190',
      8: '57375792136566',
      9: '57375792365942',
      10: '57375792595318',
      11: '57375792824694',
      12: '57375793054070',
      13: '57375793283446',
      14: '57375793512822',
      99: '57375793742198',
    },
    TITANIUM: {
      5: '57375791513974',
      6: '57375791743350',
      7: '57375791972726',
      8: '57375792202102',
      9: '57375792431478',
      10: '57375792660854',
      11: '57375792890230',
      12: '57375793119606',
      13: '57375793348982',
      14: '57375793578358',
      99: '57375793807734',
    },
  },
  [ShopifyStore.GB]: {
    BLACK: {
      5: '57375791382902',
      6: '57375791612278',
      7: '57375791841654',
      8: '57375792071030',
      9: '57375792300406',
      10: '57375792529782',
      11: '57375792759158',
      12: '57375792988534',
      13: '57375793217910',
      14: '57375793447286',
      99: '57375793676662',
    },
    GOLD: {
      5: '57375791415670',
      6: '57375791645046',
      7: '57375791874422',
      8: '57375792103798',
      9: '57375792333174',
      10: '57375792562550',
      11: '57375792791926',
      12: '57375793021302',
      13: '57375793250678',
      14: '57375793480054',
      99: '57375793709430',
    },
    SILVER: {
      5: '57375791448438',
      6: '57375791677814',
      7: '57375791907190',
      8: '57375792136566',
      9: '57375792365942',
      10: '57375792595318',
      11: '57375792824694',
      12: '57375793054070',
      13: '57375793283446',
      14: '57375793512822',
      99: '57375793742198',
    },
    TITANIUM: {
      5: '57375791513974',
      6: '57375791743350',
      7: '57375791972726',
      8: '57375792202102',
      9: '57375792431478',
      10: '57375792660854',
      11: '57375792890230',
      12: '57375793119606',
      13: '57375793348982',
      14: '57375793578358',
      99: '57375793807734',
    },
  },
  [ShopifyStore.AE]: {
    BLACK: {
      5: '62576782344563',
      6: '62576782573939',
      7: '62576782803315',
      8: '62576783032691',
      9: '62576783262067',
      10: '62576783491443',
      11: '62576783720819',
      12: '62576783950195',
      13: '62576784179571',
      14: '62576784408947',
      99: '62576784638323',
    },
    GOLD: {
      5: '62576782377331',
      6: '62576782606707',
      7: '62576782836083',
      8: '62576783065459',
      9: '62576783294835',
      10: '62576783524211',
      11: '62576783753587',
      12: '62576783982963',
      13: '62576784212339',
      14: '62576784441715',
      99: '62576784671091',
    },
    SILVER: {
      5: '62576782410099',
      6: '62576782639475',
      7: '62576782868851',
      8: '62576783098227',
      9: '62576783327603',
      10: '62576783556979',
      11: '62576783786355',
      12: '62576784015731',
      13: '62576784245107',
      14: '62576784474483',
      99: '62576784703859',
    },
    TITANIUM: {
      5: '62576782475635',
      6: '62576782705011',
      7: '62576782934387',
      8: '62576783163763',
      9: '62576783393139',
      10: '62576783622515',
      11: '62576783851891',
      12: '62576784081267',
      13: '62576784310643',
      14: '62576784540019',
      99: '62576784769395',
    },
  },
};


const CHARGER_IDS: Partial<Record<ShopifyStore, Record<string, string>>> = { // I am ashamed of what I had to do to fix this type error
  [ShopifyStore.US]: { BLACK: '42613769109600', GOLD: '42613769928800' },
  [ShopifyStore.IN]: { BLACK: '44694401548358', GOLD: '44694401908806' },
  [ShopifyStore.ROW]: { BLACK: '44021940846652', GOLD: '44021944877116' },
  [ShopifyStore.EU]: { BLACK: '57375798264182', GOLD: '57375800787318' },
  [ShopifyStore.GB]: { BLACK: '57375798264182', GOLD: '57375800787318' },
  [ShopifyStore.AE]: { BLACK: '62576792174963', GOLD: '62576798957939' },
};

const SIZES = [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 99] as const;
const RING_COLORS = ['GOLD', 'SILVER', 'BLACK', 'TITANIUM'] as const;

function buildRingVariantsData(): Record<
  string,
  Record<number, { E: StoreVariants; N: StoreVariants }>
> {
  const data: Record<
    string,
    Record<number, { E: StoreVariants; N: StoreVariants }>
  > = {
    GOLD: {},
    SILVER: {},
    BLACK: {},
    TITANIUM: {},
  };
  for (const color of RING_COLORS) {
    for (const size of SIZES) {
      const e: StoreVariants = {};
      const n: StoreVariants = {};
      for (const store of BASE_STORES) {
        const id = RING_IDS[store]?.[color]?.[size];
        if (id) {
          const entry: VariantData = {
            id: toShopifyGid(id),
            url: PLACEHOLDER_URL,
          };
          e[store] = entry;
          n[store] = entry;
        }
      }
      if (!data[color][size]) data[color][size] = { E: {}, N: {} };
      data[color][size].E = e;
      data[color][size].N = n;
    }
  }
  return data;
}

function buildChargerCaseData(): Record<string, Record<number, StoreVariants>> {
  const data: Record<string, Record<number, StoreVariants>> = {
    GOLD: {},
    BLACK: {},
  };
  for (const color of ['GOLD', 'BLACK'] as const) {
    for (const size of SIZES) {
      const sv: StoreVariants = {};
      for (const store of BASE_STORES) {
        const id = CHARGER_IDS[store]?.[color];
        if (id) {
          sv[store] = { id: toShopifyGid(id), url: PLACEHOLDER_URL };
        }
      }
      if (!data[color][size]) data[color][size] = {};
      data[color][size] = sv;
    }
  }
  return data;
}

/**
 * Ring PRO variant data (6 base stores only). Color → Size → { E, N } → Store → { id, url }
 */
export const RING_PRO_VARIANTS_DATA = buildRingVariantsData();

/**
 * Charger case variant data (6 base stores only). Color → Size → Store → { id, url }
 */
export const CHARGER_CASE_DATA = buildChargerCaseData();

/**
 * Expanded for all stores (AU, CA, SA, MX, ZA filled from base). Use this in consumers.
 */
export const RING_PRO_VARIANTS_DATA_EXPANDED = deepExpandStoreAliases(
  RING_PRO_VARIANTS_DATA,
);

export const CHARGER_CASE_DATA_EXPANDED =
  deepExpandStoreAliases(CHARGER_CASE_DATA);
